import React from 'react';
import { Box, TextField, Select, MenuItem, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

// Countries with dial codes for MENA region and common countries
const COUNTRIES = [
  { code: 'EG', name: 'Egypt', flag: '🇪🇬', dialCode: '+20' },
  { code: 'AE', name: 'UAE', flag: '🇦🇪', dialCode: '+971' },
  { code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦', dialCode: '+966' },
  { code: 'QA', name: 'Qatar', flag: '🇶🇦', dialCode: '+974' },
  { code: 'KW', name: 'Kuwait', flag: '🇰🇼', dialCode: '+965' },
  { code: 'BH', name: 'Bahrain', flag: '🇧🇭', dialCode: '+973' },
  { code: 'OM', name: 'Oman', flag: '🇴🇲', dialCode: '+968' },
  { code: 'JO', name: 'Jordan', flag: '🇯🇴', dialCode: '+962' },
  { code: 'LB', name: 'Lebanon', flag: '🇱🇧', dialCode: '+961' },
  { code: 'PS', name: 'Palestine', flag: '🇵🇸', dialCode: '+970' },
  { code: 'SY', name: 'Syria', flag: '🇸🇾', dialCode: '+963' },
  { code: 'IQ', name: 'Iraq', flag: '🇮🇶', dialCode: '+964' },
  { code: 'YE', name: 'Yemen', flag: '🇾🇪', dialCode: '+967' },
  { code: 'US', name: 'United States', flag: '🇺🇸', dialCode: '+1' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', dialCode: '+44' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦', dialCode: '+1' },
  { code: 'TR', name: 'Turkey', flag: '🇹🇷', dialCode: '+90' },
  { code: 'IN', name: 'India', flag: '🇮🇳', dialCode: '+91' },
  { code: 'PK', name: 'Pakistan', flag: '🇵🇰', dialCode: '+92' },
  { code: 'CN', name: 'China', flag: '🇨🇳', dialCode: '+86' },
  { code: 'JP', name: 'Japan', flag: '🇯🇵', dialCode: '+81' },
  { code: 'KR', name: 'South Korea', flag: '🇰🇷', dialCode: '+82' },
  { code: 'FR', name: 'France', flag: '🇫🇷', dialCode: '+33' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪', dialCode: '+49' },
  { code: 'IT', name: 'Italy', flag: '🇮🇹', dialCode: '+39' },
  { code: 'ES', name: 'Spain', flag: '🇪🇸', dialCode: '+34' },
];

// Styled select for country code
const CountrySelect = styled(Select)(({ theme }) => ({
  minWidth: 'auto',
  '& .MuiSelect-select': {
    paddingRight: '24px !important',
    paddingLeft: 0,
    paddingTop: 0,
    paddingBottom: 0,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    '&:focus': {
      backgroundColor: 'transparent',
    }
  },
  '& .MuiInput-underline:before': {
    display: 'none !important',
    border: 'none !important',
  },
  '& .MuiInput-underline:after': {
    display: 'none !important',
    border: 'none !important',
  },
  '& .MuiInput-underline:hover:before': {
    display: 'none !important',
    border: 'none !important',
  },
  '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
    display: 'none !important',
    border: 'none !important',
  },
  '& .MuiInput-underline.Mui-focused:after': {
    display: 'none !important',
    border: 'none !important',
  },
  '& .MuiSelect-icon': {
    right: 0,
    top: '50%',
    transform: 'translateY(-50%)',
  }
}));

/**
 * MagicPhoneInput - Enhanced phone input with country code selection
 * 
 * @param {Object} props - Component properties
 * @param {string} props.name - Field name
 * @param {string} props.label - Field label
 * @param {Object} props.value - Current value {countryCode: 'EG', number: '123456789'}
 * @param {Function} props.onChange - Change handler function
 * @param {Function} props.onBlur - Blur handler function
 * @param {boolean} props.error - Whether the field has an error
 * @param {string} props.helperText - Helper text to display
 * @param {boolean} props.required - Whether the field is required
 * @param {boolean} props.disabled - Whether the field is disabled
 * @param {string} props.placeholder - Placeholder text
 * @param {Object} props.InputProps - Additional props for TextField InputProps
 */
const MagicPhoneInput = ({
  name,
  label,
  value = { countryCode: 'EG', number: '' },
  onChange,
  onBlur,
  error = false,
  helperText = '',
  required = false,
  disabled = false,
  placeholder = 'Phone number',
  InputProps = {},
  ...props
}) => {
  // Ensure value is an object
  const phoneValue = typeof value === 'string' 
    ? { countryCode: 'EG', number: value }
    : value || { countryCode: 'EG', number: '' };

  const selectedCountryCode = phoneValue.countryCode || 'EG';
  const selectedCountry = COUNTRIES.find(c => c.code === selectedCountryCode) || COUNTRIES[0];

  const handleCountryChange = (event) => {
    const newCountryCode = event.target.value;
    onChange({
      ...phoneValue,
      countryCode: newCountryCode
    });
  };

  const handlePhoneChange = (event) => {
    // Only allow numbers, spaces, hyphens, and parentheses
    const inputValue = event.target.value;
    const sanitizedValue = inputValue.replace(/[^0-9\s\-()]/g, '');
    
    onChange({
      ...phoneValue,
      number: sanitizedValue
    });
  };

  return (
    <TextField
      name={name}
      label={label}
      fullWidth
      placeholder={placeholder}
      value={phoneValue.number || ''}
      onChange={handlePhoneChange}
      onBlur={onBlur}
      type="tel"
      inputMode="tel"
      error={error}
      disabled={disabled}
      required={required}
      helperText={helperText}
      InputProps={{
        ...InputProps,
        startAdornment: (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            borderRight: '1px solid',
            borderColor: 'divider',
            pr: 1,
            mr: 1,
            minWidth: 'auto'
          }}>
            <Box 
              component="button"
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // Simple country toggle for now
                const currentIndex = COUNTRIES.findIndex(c => c.code === selectedCountryCode);
                const nextIndex = (currentIndex + 1) % COUNTRIES.length;
                handleCountryChange({ target: { value: COUNTRIES[nextIndex].code } });
              }}
              sx={{
                background: 'none',
                border: 'none',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                fontSize: '0.875rem',
                '&:hover': {
                  opacity: 0.7
                }
              }}
            >
              <span>{selectedCountry.flag}</span>
              <Typography variant="caption" component="span">
                {selectedCountry.dialCode}
              </Typography>
            </Box>
          </Box>
        ),
      }}
      {...props}
    />
  );
};

export default MagicPhoneInput;